import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ============================================================================
// REPORT TYPES AND FREQUENCIES
// ============================================================================

export const reportTypes = ["daily_sales", "weekly_inventory", "monthly_financial", "quarterly_review", "custom"] as const;
export type ReportType = typeof reportTypes[number];

export const reportFrequencies = ["daily", "weekly", "monthly", "quarterly", "on_demand"] as const;
export type ReportFrequency = typeof reportFrequencies[number];

export const reportFormats = ["pdf", "csv", "json", "html"] as const;
export type ReportFormat = typeof reportFormats[number];

export const reportStatuses = ["pending", "generating", "completed", "failed", "archived"] as const;
export type ReportStatus = typeof reportStatuses[number];

// ============================================================================
// REPORT TEMPLATES TABLE
// Configurable templates for different report types
// ============================================================================

export const reportTemplates = pgTable("report_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  reportType: text("report_type").notNull(), // daily_sales, weekly_inventory, etc.
  // Template configuration
  config: jsonb("config").$type<ReportTemplateConfig>().notNull(),
  // Scheduling
  frequency: text("frequency").notNull(), // daily, weekly, monthly, quarterly, on_demand
  scheduleHour: integer("schedule_hour").default(8), // Hour of day to run (0-23)
  scheduleDayOfWeek: integer("schedule_day_of_week"), // 0-6 for weekly reports
  scheduleDayOfMonth: integer("schedule_day_of_month"), // 1-31 for monthly reports
  scheduleQuarterMonth: integer("schedule_quarter_month"), // 1, 4, 7, 10 for quarterly
  // Distribution
  recipientEmails: text("recipient_emails").array(),
  slackWebhook: text("slack_webhook"),
  // Status
  isActive: boolean("is_active").default(true),
  lastRunAt: timestamp("last_run_at"),
  nextRunAt: timestamp("next_run_at"),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("report_templates_type_idx").on(table.reportType),
  index("report_templates_frequency_idx").on(table.frequency),
  index("report_templates_next_run_idx").on(table.nextRunAt),
]);

export interface ReportTemplateConfig {
  // Data sections to include
  sections: ReportSection[];
  // Filters
  filters?: {
    dateRange?: "today" | "yesterday" | "last_7_days" | "last_30_days" | "last_quarter" | "last_year" | "custom";
    customStartDate?: string;
    customEndDate?: string;
    biomes?: string[];
    statuses?: string[];
    customerSegments?: string[];
  };
  // Comparison settings
  comparison?: {
    enabled: boolean;
    periodType: "previous_period" | "same_period_last_year";
  };
  // Formatting
  format: ReportFormat;
  includeCharts: boolean;
  includeSummary: boolean;
  // Custom fields for ad-hoc reports
  customMetrics?: CustomMetric[];
  groupBy?: string[];
}

export interface ReportSection {
  id: string;
  name: string;
  type: "summary" | "table" | "chart" | "metrics" | "text";
  dataSource: string; // orders, subscriptions, inventory, customers, etc.
  metrics?: string[];
  columns?: string[];
  chartType?: "bar" | "line" | "pie" | "area";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  limit?: number;
}

export interface CustomMetric {
  id: string;
  name: string;
  formula: string; // e.g., "revenue / orders" for AOV
  format: "number" | "currency" | "percentage";
}

export const insertReportTemplateSchema = createInsertSchema(reportTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  reportType: z.enum(reportTypes),
  config: z.object({
    sections: z.array(z.object({
      id: z.string(),
      name: z.string(),
      type: z.enum(["summary", "table", "chart", "metrics", "text"]),
      dataSource: z.string(),
      metrics: z.array(z.string()).optional(),
      columns: z.array(z.string()).optional(),
      chartType: z.enum(["bar", "line", "pie", "area"]).optional(),
      sortBy: z.string().optional(),
      sortOrder: z.enum(["asc", "desc"]).optional(),
      limit: z.number().optional(),
    })),
    filters: z.object({
      dateRange: z.enum(["today", "yesterday", "last_7_days", "last_30_days", "last_quarter", "last_year", "custom"]).optional(),
      customStartDate: z.string().optional(),
      customEndDate: z.string().optional(),
      biomes: z.array(z.string()).optional(),
      statuses: z.array(z.string()).optional(),
      customerSegments: z.array(z.string()).optional(),
    }).optional(),
    comparison: z.object({
      enabled: z.boolean(),
      periodType: z.enum(["previous_period", "same_period_last_year"]),
    }).optional(),
    format: z.enum(reportFormats),
    includeCharts: z.boolean(),
    includeSummary: z.boolean(),
    customMetrics: z.array(z.object({
      id: z.string(),
      name: z.string(),
      formula: z.string(),
      format: z.enum(["number", "currency", "percentage"]),
    })).optional(),
    groupBy: z.array(z.string()).optional(),
  }),
  frequency: z.enum(reportFrequencies),
  scheduleHour: z.number().min(0).max(23).default(8),
  scheduleDayOfWeek: z.number().min(0).max(6).optional(),
  scheduleDayOfMonth: z.number().min(1).max(31).optional(),
  scheduleQuarterMonth: z.number().optional(),
  recipientEmails: z.array(z.string().email()).optional(),
  slackWebhook: z.string().url().optional(),
  isActive: z.boolean().default(true),
});

export type ReportTemplate = typeof reportTemplates.$inferSelect;
export type InsertReportTemplate = z.infer<typeof insertReportTemplateSchema>;

// ============================================================================
// GENERATED REPORTS TABLE
// Stores generated report instances with data snapshots
// ============================================================================

export const generatedReports = pgTable("generated_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: varchar("template_id").references(() => reportTemplates.id),
  name: text("name").notNull(),
  reportType: text("report_type").notNull(),
  // Report period
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  // Status
  status: text("status").notNull().default("pending"),
  // Generated content
  data: jsonb("data").$type<ReportData>(),
  summary: text("summary"), // AI-generated summary
  // File storage
  fileUrl: text("file_url"),
  fileFormat: text("file_format"),
  fileSizeBytes: integer("file_size_bytes"),
  // Distribution tracking
  distributedAt: timestamp("distributed_at"),
  recipientCount: integer("recipient_count").default(0),
  // Error handling
  errorMessage: text("error_message"),
  retryCount: integer("retry_count").default(0),
  // Metadata
  generationTimeMs: integer("generation_time_ms"),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  archivedAt: timestamp("archived_at"),
}, (table) => [
  index("generated_reports_template_idx").on(table.templateId),
  index("generated_reports_type_idx").on(table.reportType),
  index("generated_reports_status_idx").on(table.status),
  index("generated_reports_period_idx").on(table.periodStart, table.periodEnd),
  index("generated_reports_created_idx").on(table.createdAt),
]);

export interface ReportData {
  // Summary metrics
  summary?: {
    [key: string]: number | string;
  };
  // Data sections
  sections: ReportDataSection[];
  // Comparison data
  comparison?: {
    previousPeriod: {
      [key: string]: number | string;
    };
    changes: {
      [key: string]: {
        value: number;
        percentage: number;
        trend: "up" | "down" | "flat";
      };
    };
  };
  // Metadata
  generatedAt: string;
  dataAsOf: string;
}

export interface ReportDataSection {
  sectionId: string;
  name: string;
  type: "summary" | "table" | "chart" | "metrics" | "text";
  data: unknown;
  chartData?: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor?: string[];
    }[];
  };
}

export const insertGeneratedReportSchema = createInsertSchema(generatedReports).omit({
  id: true,
  createdAt: true,
}).extend({
  templateId: z.string().uuid().optional(),
  name: z.string().min(1),
  reportType: z.enum(reportTypes),
  periodStart: z.date(),
  periodEnd: z.date(),
  status: z.enum(reportStatuses).default("pending"),
  data: z.any().optional(),
  summary: z.string().optional(),
  fileUrl: z.string().url().optional(),
  fileFormat: z.enum(reportFormats).optional(),
  fileSizeBytes: z.number().optional(),
  errorMessage: z.string().optional(),
});

export type GeneratedReport = typeof generatedReports.$inferSelect;
export type InsertGeneratedReport = z.infer<typeof insertGeneratedReportSchema>;

// ============================================================================
// REPORT SUBSCRIPTIONS TABLE
// User subscriptions to receive specific reports
// ============================================================================

export const reportSubscriptions = pgTable("report_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: varchar("template_id").notNull().references(() => reportTemplates.id),
  email: text("email").notNull(),
  name: text("name"),
  // Preferences
  format: text("format").default("pdf"),
  timezone: text("timezone").default("America/New_York"),
  // Status
  isActive: boolean("is_active").default(true),
  unsubscribeToken: text("unsubscribe_token").unique(),
  // Tracking
  lastSentAt: timestamp("last_sent_at"),
  sendCount: integer("send_count").default(0),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("report_subscriptions_template_idx").on(table.templateId),
  index("report_subscriptions_email_idx").on(table.email),
]);

export const insertReportSubscriptionSchema = createInsertSchema(reportSubscriptions).omit({
  id: true,
  createdAt: true,
}).extend({
  templateId: z.string().uuid(),
  email: z.string().email(),
  name: z.string().optional(),
  format: z.enum(reportFormats).default("pdf"),
  timezone: z.string().default("America/New_York"),
  isActive: z.boolean().default(true),
});

export type ReportSubscription = typeof reportSubscriptions.$inferSelect;
export type InsertReportSubscription = z.infer<typeof insertReportSubscriptionSchema>;

// ============================================================================
// ANALYTICS INTERFACES
// Types for report data collection
// ============================================================================

export interface DailySalesMetrics {
  date: string;
  totalRevenue: number;
  orderCount: number;
  averageOrderValue: number;
  newCustomers: number;
  returningCustomers: number;
  topProducts: { name: string; revenue: number; count: number }[];
  revenueByBiome: { biome: string; revenue: number }[];
  conversionRate: number;
  refundAmount: number;
}

export interface WeeklyInventoryMetrics {
  weekStart: string;
  weekEnd: string;
  totalSpecies: number;
  totalPlants: number;
  plantsInQuarantine: number;
  lowStockAlerts: { speciesId: string; name: string; current: number; minimum: number }[];
  mortalityRate: number;
  healthDistribution: { grade: string; count: number }[];
  acquisitions: number;
  allocations: number;
  speciesPopulation: { species: string; count: number }[];
}

export interface MonthlyFinancialMetrics {
  month: string;
  year: number;
  // Revenue
  grossRevenue: number;
  refunds: number;
  netRevenue: number;
  // Subscriptions
  subscriptionRevenue: number;
  newSubscriptions: number;
  churnedSubscriptions: number;
  mrr: number; // Monthly Recurring Revenue
  // Breakdown
  revenueByProduct: { product: string; revenue: number }[];
  revenueByChannel: { channel: string; revenue: number }[];
  // Growth
  revenueGrowth: number; // vs previous month
  yearOverYearGrowth: number;
  // Customer metrics
  customerAcquisitionCost: number;
  lifetimeValue: number;
}

export interface QuarterlyBusinessReview {
  quarter: string;
  year: number;
  // Financial
  totalRevenue: number;
  totalExpenses: number;
  grossMargin: number;
  netMargin: number;
  // Growth
  quarterlyGrowth: number;
  yearOverYearGrowth: number;
  // Customer
  totalCustomers: number;
  newCustomers: number;
  churnRate: number;
  nps: number; // Net Promoter Score
  // Product
  topProducts: { name: string; revenue: number; growth: number }[];
  productMix: { category: string; percentage: number }[];
  // Operations
  averageFulfillmentTime: number;
  customerSatisfaction: number;
  // Goals
  goalsVsActual: { goal: string; target: number; actual: number; status: string }[];
}
